<template>
  <div class="ren-decoration-items">
    <div class="ren-decoration-field-group">
      <label>默认选中项</label>
      <RrInputNumber
        :value="activeIndex"
        :min="0"
        :max="Math.max(0, items.length - 1)"
        :disabled="readonly || items.length === 0"
        @update:value="onActiveIndexChange"
      />
    </div>
    <div class="ren-decoration-banner-items-editor">
      <div class="ren-decoration-banner-items-editor__head">
        <strong>菜单项（{{ items.length }} 项）</strong>
        <RrButton type="primary" size="small" :disabled="readonly || items.length >= 12" @click="addItem">
          添加项
        </RrButton>
      </div>
      <article v-for="(item, index) in items" :key="index" class="ren-decoration-banner-item-card">
        <header class="banner-item-card__head">
          <span>第 {{ index + 1 }} 项</span>
          <div class="banner-item-card__actions">
            <RrButton size="small" type="default" :disabled="readonly || index === 0" title="上移" aria-label="上移" @click="moveItem(index, -1)">
              <ArrowUp :size="12" />
            </RrButton>
            <RrButton size="small" type="default" :disabled="readonly || index === items.length - 1" title="下移" aria-label="下移" @click="moveItem(index, 1)">
              <ArrowDown :size="12" />
            </RrButton>
            <RrButton size="small" type="primary" danger :disabled="readonly" title="删除" aria-label="删除" @click="removeItem(index)">
              <Trash2 :size="12" />
            </RrButton>
          </div>
        </header>
        <div class="banner-item-card__fields">
          <RrInput
            placeholder="名称"
            :disabled="readonly"
            :value="String(item.title ?? item.label ?? '')"
            @update:value="(val) => setItem(index, 'title', String(val ?? ''))"
          />
          <div class="banner-item-card__link">
            <span>{{ String(item.link || '') || '未配置跳转' }}</span>
            <RrButton size="small" type="default" :disabled="readonly" @click="emit('pick-item-link', index)">
              选择页面
            </RrButton>
          </div>
        </div>
      </article>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { ArrowDown, ArrowUp, Trash2 } from '@lucide/vue'
import { RrButton, RrInput, RrInputNumber } from '@xjlv2/ui'
import { recordList } from '../../../previews/preview-helpers'
import { mergeWidgetProps, type WidgetSettingsEmits, type WidgetSettingsProps } from '../../types'

const props = withDefaults(defineProps<WidgetSettingsProps>(), {
  readonly: false
})

const emit = defineEmits<WidgetSettingsEmits>()

const items = computed(() => recordList(props.component.props?.items))
const activeIndex = computed(() => {
  const value = Number(props.component.props?.activeIndex)
  return Number.isFinite(value) ? value : 0
})

function patch(partial: Record<string, unknown>) {
  emit('update:props', mergeWidgetProps(props.component, partial))
}

function onActiveIndexChange(value: number | null) {
  const max = Math.max(0, items.value.length - 1)
  patch({ activeIndex: value == null ? 0 : Math.max(0, Math.min(max, Math.round(value))) })
}

function emitItems(next: Record<string, unknown>[]) {
  patch({ items: next })
}

function setItem(index: number, key: string, value: unknown) {
  emitItems(items.value.map((item, itemIndex) => (itemIndex === index ? { ...item, [key]: value } : item)))
}

function addItem() {
  if (items.value.length >= 12) return
  emitItems([...items.value, { title: `菜单 ${items.value.length + 1}`, link: '' }])
}

function removeItem(index: number) {
  emitItems(items.value.filter((_, itemIndex) => itemIndex !== index))
}

function moveItem(index: number, offset: -1 | 1) {
  const target = index + offset
  if (target < 0 || target >= items.value.length) return
  const next = [...items.value]
  const [moved] = next.splice(index, 1)
  next.splice(target, 0, moved)
  emitItems(next)
}
</script>
